import { createContext, useContext, useState, useEffect, type ReactNode } from "react"
import { useKeycloak } from "@react-keycloak/web"
import type { KeycloakTokenParsed } from "keycloak-js"

/**
 * User information extracted from Keycloak token
 */
export interface KeycloakUser {
  id: string
  username: string
  email: string
  firstName: string
  lastName: string
  name: string // Full name (firstName + lastName)
}

/**
 * User role types for the application
 */
export type UserRole = "admin" | "employee"

/**
 * Authentication context interface
 */
interface AuthContextType {
  user: KeycloakUser | null
  selectedRole: UserRole | null
  availableRoles: UserRole[]
  isAuthenticated: boolean
  isLoading: boolean
  logout: () => void
  setSelectedRole: (role: UserRole) => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

/**
 * Extended Keycloak token with custom claims
 */
interface ExtendedKeycloakTokenParsed extends KeycloakTokenParsed {
  preferred_username?: string
  email?: string
  given_name?: string
  family_name?: string
  realm_access?: {
    roles: string[]
  }
  resource_access?: {
    [key: string]: {
      roles: string[]
    }
  }
}

/**
 * Extract user information from Keycloak token
 *
 * @param tokenParsed - Parsed Keycloak token
 * @returns KeycloakUser object or null if token is invalid
 */
function extractUserFromToken(tokenParsed: ExtendedKeycloakTokenParsed | undefined): KeycloakUser | null {
  if (!tokenParsed) return null

  const firstName = tokenParsed.given_name || ""
  const lastName = tokenParsed.family_name || ""

  return {
    id: tokenParsed.sub || "",
    username: tokenParsed.preferred_username || "",
    email: tokenParsed.email || "",
    firstName,
    lastName,
    name: `${firstName} ${lastName}`.trim() || tokenParsed.preferred_username || "User",
  }
}

/**
 * Extract available roles from Keycloak token
 * Checks both realm roles and client-specific roles (resource_access)
 *
 * @param tokenParsed - Parsed Keycloak token
 * @returns Array of user roles (admin, employee)
 */
function extractRoles(tokenParsed: ExtendedKeycloakTokenParsed | undefined): UserRole[] {
  if (!tokenParsed) {
    return []
  }

  const roles: UserRole[] = []
  let keycloakRoles: string[] = []

  // First, try to get roles from resource_access['remote-tracker']
  // This is for client-specific roles
  if (tokenParsed.resource_access && tokenParsed.resource_access["remote-tracker"]) {
    keycloakRoles = tokenParsed.resource_access["remote-tracker"].roles || []
  }

  // If no client roles found, fall back to realm roles
  if (keycloakRoles.length === 0 && tokenParsed.realm_access?.roles) {
    keycloakRoles = tokenParsed.realm_access.roles
  }

  // Extract our application roles
  if (keycloakRoles.includes("remote_admin")) {
    roles.push("admin")
  }
  if (keycloakRoles.includes("remote_employee")) {
    roles.push("employee")
  }



  return roles
}

interface AuthProviderProps {
  children: ReactNode
}

/**
 * AuthProvider Component
 *
 * Provides authentication state and user information throughout the application.
 * Extracts user data from Keycloak tokens and manages role selection.
 *
 * @param children - Child components to be wrapped with authentication context
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const { keycloak, initialized } = useKeycloak()
  const [selectedRole, setSelectedRole] = useState<UserRole | null>(null)

  // Extract user and roles from Keycloak token
  const user = extractUserFromToken(keycloak.tokenParsed as ExtendedKeycloakTokenParsed)
  const availableRoles = extractRoles(keycloak.tokenParsed as ExtendedKeycloakTokenParsed)

  /**
   * Auto-select role if user has only one role
   */
  useEffect(() => {
    if (availableRoles.length === 1 && !selectedRole) {
      setSelectedRole(availableRoles[0])
    }
  }, [availableRoles, selectedRole])

  /**
   * Reset selected role if user loses that role
   */
  useEffect(() => {
    if (selectedRole && !availableRoles.includes(selectedRole)) {
      setSelectedRole(null)
    }
  }, [availableRoles, selectedRole])

  /**
   * Logout function that ends Keycloak session
   */
  const logout = () => {
    keycloak.logout({
      redirectUri: window.location.origin,
    })
  }

  const value: AuthContextType = {
    user,
    selectedRole,
    availableRoles,
    isAuthenticated: keycloak.authenticated || false,
    isLoading: !initialized,
    logout,
    setSelectedRole,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/**
 * Hook to access authentication context
 *
 * @returns Authentication context
 * @throws Error if used outside AuthProvider
 */
export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}

